import React, { FormEvent, useEffect, useState } from 'react';

import useSaveNotificationParameter from '../../../hooks/notification_parameters/useSaveNotificationParameter';
import useDeleteNotificationParameter from '../../../hooks/notification_parameters/useDeleteNotificationParameter';

import { INotificationParameter, ISaveNotificationParameter } from '../../../types/custom_notifications';
import { IMetaParameter, ParameterType } from '../../../types/meta_paramter';

import { Icons } from '../../../data/Assets';

import { SVGIcon } from '../../wrappers/SVGIcon';

interface INotificationParameterProps {
    notificationParameter: INotificationParameter;
    currOption: number[];
    setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
    isDelete: boolean;
    setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
    setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
    setIsResetNew: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteModalMessage: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
    relatedMetaParameters: IMetaParameter[];
}

const NotificationParameter = ({ notificationParameter, currOption, setCurrOption, isDelete, setIsDelete, setIsOpenDeleteModal, setIsResetNew, setDeleteModalMessage, relatedMetaParameters }: INotificationParameterProps) => {
    const [title, setTitle] = useState<string>(notificationParameter.id ? notificationParameter.name : 'New Notification Parameter');

    const [currentNotificationParameter, setCurrentNotificationParameter] = useState<INotificationParameter>(notificationParameter);


    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [apiError, setApiError] = useState<string | null>(null); //eslint-disable-line
    const [parameterValue, setParameterValue] = useState<any | any[]>('');
    const [isArray, setIsArray] = useState<boolean>(false);

    useEffect(() => {
        const metaParameter = relatedMetaParameters.find(metaParameter => metaParameter.name === currentNotificationParameter.name);
        if (!metaParameter) return;

        if (currentNotificationParameter.value) {
            setParameterValue(metaParameter.type === ParameterType.ARRAY ? JSON.parse(currentNotificationParameter.value) : currentNotificationParameter.value);
        } else {
            if (metaParameter.type === ParameterType.ARRAY) {
                setParameterValue(['']);
            } else {
                setParameterValue('');
            }
        }
    }, [currentNotificationParameter.value]); //eslint-disable-line

    useEffect(() => {
        setCurrentNotificationParameter(notificationParameter);
        setTitle(notificationParameter.id ? notificationParameter.name : 'New Notification Parameter');

        try {
            const metaParameter = relatedMetaParameters.find(metaParameter => metaParameter.name === currentNotificationParameter.name);
            if (!metaParameter) return;

            const value = JSON.parse(notificationParameter.value);
            if (metaParameter.type === ParameterType.ARRAY) {
                if (!Array.isArray(value) || !value?.length) addValueField();
            }

            setIsArray(Array.isArray(value));
            if (!value) setParameterValue('');
            else setParameterValue(value);
        } catch (error) { };
    }, [notificationParameter]); // eslint-disable-line


    const { mutate: saveNotificationParameter } = useSaveNotificationParameter();
    const { mutate: deleteNotificationParameter } = useDeleteNotificationParameter();

    useEffect(() => {
        if (isDelete) {
            if (currOption.length - 1 === 3) {
                setCurrOption([currOption[0], currOption[1], currOption[2]]);
            } else {
                setCurrOption([currOption[0], currOption[1], currOption[2], 0]);
            }
            if (!notificationParameter.id) return;
            deleteNotificationParameter({
                id: notificationParameter.id,
                setIsProcessing,
                setApiError
            });
            setIsDelete(false);
        }
    }, [isDelete]); //eslint-disable-line

    if (!currentNotificationParameter) {
        return <p>Loading...</p>
    }

    const handleSave = (e: FormEvent) => {
        e.preventDefault();

        const payload: ISaveNotificationParameter = {
            id: currentNotificationParameter.id || null,
            notification_function_id: currentNotificationParameter.notification_function_id,
            name: currentNotificationParameter.name,
            description: currentNotificationParameter.description ?? relatedMetaParameters.find(metaParameter => metaParameter.name === currentNotificationParameter.name)?.description,
            value: Array.isArray(parameterValue) ? JSON.stringify(parameterValue) : parameterValue.toString()
        };

        saveNotificationParameter({
            data: payload,
            setIsProcessing,
            setApiError
        });

        setTitle(currentNotificationParameter.id ? currentNotificationParameter.name : 'New Notification Parameter');
    }

    const handleDelete = () => {
        if (!notificationParameter.id) {
            setIsResetNew(true);
            if (currOption.length - 1 === 3) {
                setCurrOption([currOption[0], currOption[1], currOption[2]]);
            } else {
                setCurrOption([currOption[0], currOption[1], currOption[2], 0]);
            }
        } else {
            setIsOpenDeleteModal(true);
            setDeleteModalMessage({ title: 'Delete Parameter', subtitle: 'This action is irreversible. Are you sure you want to delete?' });
        }
    }

    const addValueField = () => {
        const metaParameter = relatedMetaParameters.find(metaParameter => metaParameter.name === currentNotificationParameter.name);
        if (!metaParameter) return;

        if (metaParameter.type === ParameterType.ARRAY) {
            if (!isArray || !parameterValue?.length) {
                setIsArray(true);
                setParameterValue(['']);
            } else {
                setParameterValue(['', ...parameterValue]);
            }
        }
    }

    const renderValue = (type: string | undefined) => {
        if (!type) return null;

        switch (type) {
            case ParameterType.STRING:
                // setParameterValue('');
                return <input placeholder='Enter value' type='text' id='value' name='value' value={parameterValue ? parameterValue as string : ''} onChange={(e) => setParameterValue(e.target.value)} />;

            case ParameterType.BOOLEAN:
                return <select id='value' name='value' value={(parameterValue.toString() === '0' || 0) ? '0' : '1'} onChange={(e) => setParameterValue((e.target.value === '0' || 0) ? '0' : '1')}>
                    <option value={'1'}>True</option>
                    <option value={'0'}>False</option>
                </select>;

            case ParameterType.DATE:
                return <input type='date' id='value' name='value' value={parameterValue ? new Date(parameterValue as string).toDateString() : ''} onChange={(e) => setParameterValue(e.target.value)} />;

            case ParameterType.INT:
                return <input placeholder='Enter value' type='number' id='value' name='value' value={parameterValue ? parameterValue as number : ''} onChange={(e) => setParameterValue(parseInt(e.target.value))} />;

            case ParameterType.FLOAT:
                return <input placeholder='Enter value' type='number' id='value' name='value' value={parameterValue ? parameterValue as number : ''} onChange={(e) => setParameterValue(parseFloat(e.target.value))} />;

            case ParameterType.ARRAY:
                if (isArray && parameterValue) {
                    return parameterValue.map((value: string, index: number) => {

                        return (
                            <div className='field__action' key={index}>
                                <input placeholder='Enter value' type='text' id='value' name='value' value={value} onChange={(e) => setParameterValue([...parameterValue.slice(0, index), e.target.value, ...parameterValue.slice(index + 1)])} />
                                <button className='field__btn delete' type="button" onClick={() => setParameterValue([...parameterValue.slice(0, index), ...parameterValue.slice(index + 1)])}><SVGIcon src={Icons.TrashFill} color='error' /></button>
                            </div>
                        );
                    });
                } else return addValueField();

            default:
                return null;
        }
    }

    return (
        <div className='imm_admin__content'>
            <div className='content_head'>
                <h3>{title}</h3>
            </div>

            <form onSubmit={handleSave} className='content_form'>
                <div className='field'>
                    <label htmlFor='meta_parameter'>Meta Parameter <span className='field__required'>*</span></label>
                    <select id='meta_parameter' name='meta_parameter' value={currentNotificationParameter.name || ''} onChange={(e) => setCurrentNotificationParameter({ ...currentNotificationParameter, name: e.target.value })} >
                        <option value={''} disabled>Select meta parameter</option>
                        {relatedMetaParameters.length > 0 && relatedMetaParameters.map(metaParameter => {
                            return <option key={metaParameter.name} value={metaParameter.name}>{metaParameter.name}</option>
                        })}

                        {relatedMetaParameters.length === 0 && <option value={-2} disabled>No Meta Parameters</option>}
                    </select>
                </div>

                <div className='field'>
                    <label htmlFor='meta_description'>Meta Description</label>
                    <textarea id='meta_description' name='meta_description' disabled placeholder='Enter Description' value={relatedMetaParameters?.find(metaParameter => metaParameter.name === currentNotificationParameter.name)?.description || ""}></textarea>
                </div>

                <div className='field'>
                    <label htmlFor='type'>Type</label>
                    <select disabled id='type' name='type' value={relatedMetaParameters?.find(metaParameter => metaParameter.name === currentNotificationParameter.name)?.type || ParameterType.DEFAULT} onChange={() => { }}>
                        <option value={ParameterType.DEFAULT} disabled>Select Type</option>
                        <option value={ParameterType.STRING}>String</option>
                        <option value={ParameterType.BOOLEAN}>Boolean</option>
                        <option value={ParameterType.DATE}>Date</option>
                        <option value={ParameterType.INT}>Int</option>
                        <option value={ParameterType.FLOAT}>Float</option>
                        <option value={ParameterType.ARRAY}>List</option>
                    </select>
                </div>

                <div className='field'>
                    <label htmlFor='description'>Description <span className='field__required'>*</span></label>
                    <textarea id='description' name='description' placeholder='Enter Description' value={currentNotificationParameter.description || ""} onChange={(e) => setCurrentNotificationParameter({ ...currentNotificationParameter, description: e.target.value })}></textarea>
                </div>

                {currentNotificationParameter ? (<div className='field'>
                    {relatedMetaParameters?.find(metaParameter => metaParameter.name === currentNotificationParameter.name)?.type === ParameterType.ARRAY ? <div className='field__action'>
                        <label htmlFor='value'>Value <span className='field__required'>*</span></label>
                        <button className='field__btn' type='button' onClick={addValueField}><SVGIcon src={Icons.Plus} /></button>
                    </div> : <label htmlFor='value'>Value <span className='field__required'>*</span></label>}

                    {renderValue(relatedMetaParameters?.find(metaParameter => metaParameter.name === currentNotificationParameter.name)?.type)}
                </div>) : null}

                <div className='actions'>
                    <button className='delete_btn' disabled={isProcessing} type="button" onClick={handleDelete}>{!notificationParameter.id ? 'Cancel' : 'Delete'}</button>
                    <button className='save_btn' type='submit'>{isProcessing ? <i className='imm_admin__process_loader' /> : !notificationParameter.id ? 'Create' : 'Save'}</button>
                </div>
            </form>
        </div>
    );
}

export default NotificationParameter;